import { useState, useCallback, useEffect, useRef } from 'react'
import { PIXELS_PER_UNIT, useGameStore } from '@/store/gameStore'
import type { ServerSessionPayload, ServerRollResultPayload } from '@/store/gameStore'
import { useSessionStore } from '@/store/sessionStore'
import { useAudioContext } from '@/contexts/AudioContext'
import { bgMobile } from '@/assets/backgrounds'
import { cartoonBird } from '@/assets/ui'
import { rollTypeToVariant } from '@/types/rollMapping'

// Game components
import Chopsticks from '@/components/Game/Chopsticks'
import Tower from '@/components/Game/Tower'
import Plate from '@/components/Game/Plate'
import FallingRoll from '@/components/Game/FallingRoll'

// UI components
import BetPanel from '@/components/UI/BetPanel'
import ControlPanel from '@/components/UI/ControlPanel'
import HistoryPanel from '@/components/UI/HistoryPanel'
import BottomPanel from '@/components/UI/BottomPanel'
import HotRollsBanner from '@/components/UI/HotRollsBanner'
import Shop from '@/components/UI/Shop'
import ResultBanner from '@/components/UI/ResultBanner'
import InfoModal from '@/components/UI/InfoModal'

import { GAME_CONFIG, RollVariant, ROLL_DIMENSIONS } from '@/types'

// Effects
import SlidingDoors from '@/components/Effects/SlidingDoors'

// DevTools
import { DoorConfig, defaultDoorConfig } from '@/components/DevTools/DoorEditor'
import { FallingRollConfig, defaultFallingRollConfig } from '@/components/DevTools/FallingRollEditor'
import { ChopsticksConfigType, DEFAULT_CONFIG } from '@/components/Game/Chopsticks'
import { defaultPlateConfig } from '@/components/DevTools/PlateEditor'

// WebSocket
import { createGameWs, destroyGameWs, getGameWs, type GameWebSocketService } from '@/services/gameWebSocket'
import { getGameWsBaseUrl } from '@/services/backendConfig'
import type { ServerEvent } from '@/services/gameWebSocket'

type BottomPanelMode = 'bet' | 'control' | 'history'

interface DroppingRoll {
  variant: RollVariant
  isHot: boolean
  startX: number
  targetY: number
  isMiss?: boolean
  isEdgeMiss?: boolean
  isRollOff?: boolean
  isWipeout?: boolean
  fallDuration?: number
  horizontalVelocity?: number
  // Analytical landing X computed from server cycle params (matches server math).
  landingXOverride?: number
}

const BASE_FALL_DURATION = 600
const ROLL_OFF_ANIMATION_TIME = BASE_FALL_DURATION + 1250
const DEMO_ROLL_OFF_CHANCE = 0.05
const TOWER_EXTRA_OFFSET = 40
const CRASH_BIRD_DURATION = 1150
const CRASH_BIRD_CONTACT_DELAY = 550
const CALCULATOR_PRESETS_MINOR = [50, 100, 250]
const DEFAULT_BET_MINOR = CALCULATOR_PRESETS_MINOR[1]

// Backend WS URL (from env or default to same host)
const WS_BASE_URL = getGameWsBaseUrl()
// WS auth token — in local/dev mode use "player_id|currency|balance" format
const WS_TOKEN = import.meta.env.VITE_GAME_WS_TOKEN || ''

export type GameConnectionStatus = 'connecting' | 'connected' | 'ready' | 'error'

interface MainScreenProps {
  connectionRetryKey?: number
  onConnectionStatusChange?: (status: GameConnectionStatus, message?: string) => void
}

function connectionErrorMessage(code: string, fallback: string): string {
  if (code === 'AUTH_FAILED' || code === 'AUTH_REQUIRED') return 'SESSION UNAVAILABLE'
  if (code === 'SESSION_REPLACED') return 'SESSION OPENED ELSEWHERE'
  if (code === 'UNSUPPORTED_CURRENCY') return 'CURRENCY NOT SUPPORTED'
  if (code === 'INVALID_ACCOUNT' || code === 'SESSION_INIT_FAILED') return 'SESSION UNAVAILABLE'
  return fallback || 'CONNECTION FAILED'
}

function isConnectionSetupError(code: string): boolean {
  return ['AUTH_FAILED', 'AUTH_REQUIRED', 'SESSION_REPLACED', 'UNSUPPORTED_CURRENCY', 'INVALID_ACCOUNT', 'SESSION_INIT_FAILED'].includes(code)
}

export default function MainScreen({ connectionRetryKey = 0, onConnectionStatusChange }: MainScreenProps) {
  const { fadeToMusic, playSfx, vibrateOnLand } = useAudioContext()

  // === UI State ===
  const [bottomMode, setBottomMode] = useState<BottomPanelMode>('bet')
  const [isShopOpen, setIsShopOpen] = useState(false)
  const [isInfoOpen, setIsInfoOpen] = useState(false)
  const [isDoorOpen, setIsDoorOpen] = useState(false)
  const [showResult, setShowResult] = useState(false)
  const [showTutorial, setShowTutorial] = useState(true)
  const [resultType, setResultType] = useState<'win' | 'lose'>('lose')
  const [resultAmount, setResultAmount] = useState(0)
  const [crashEffect, setCrashEffect] = useState<'earthquake' | 'bird' | null>(null)

  // === Game State ===
  const chopsticksPositionRef = useRef(0)
  const chopsticksVelocityRef = useRef(0)
  const prevChopPosRef = useRef(0)
  const prevChopTimeRef = useRef(Date.now())

  const handleChopsticksPosition = useCallback((pos: number) => {
    const now = Date.now()
    const dt = (now - prevChopTimeRef.current) / 1000
    if (dt > 0 && dt < 0.5) {
      chopsticksVelocityRef.current = (pos - prevChopPosRef.current) / dt
    }
    prevChopPosRef.current = pos
    prevChopTimeRef.current = now
    chopsticksPositionRef.current = pos
  }, [])

  const [isDropping, setIsDropping] = useState(false)
  const [droppingRoll, setDroppingRoll] = useState<DroppingRoll | null>(null)
  const [currentRollVariant, setCurrentRollVariant] = useState<RollVariant>(1)
  const [isCollapsing, setIsCollapsing] = useState(false)
  const [wsConnected, setWsConnected] = useState(false)
  const [wsError, setWsError] = useState<string | null>(null)

  // === DevTools Config ===
  const [chopsticksConfig] = useState<ChopsticksConfigType>(DEFAULT_CONFIG)
  const [plateConfig] = useState(defaultPlateConfig)
  const [doorConfig] = useState<DoorConfig>(defaultDoorConfig)
  const [fallingRollConfig] = useState<FallingRollConfig>(defaultFallingRollConfig)

  const dropLockRef = useRef(false)
  const dropWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const crashEffectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const crashCollapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const terminalConnectionErrorRef = useRef<string | null>(null)

  const clearDropWatchdog = useCallback(() => {
    if (!dropWatchdogRef.current) return
    clearTimeout(dropWatchdogRef.current)
    dropWatchdogRef.current = null
  }, [])

  const unlockDrop = useCallback(() => {
    clearDropWatchdog()
    setIsDropping(false)
    dropLockRef.current = false
    setDroppingRoll(null)
  }, [clearDropWatchdog])

  const triggerCrashEffect = useCallback((forceEffect?: 'earthquake') => {
    const nextEffect = forceEffect ?? (Math.random() < 0.5 ? 'earthquake' : 'bird')
    if (crashEffectTimerRef.current) clearTimeout(crashEffectTimerRef.current)
    if (crashCollapseTimerRef.current) clearTimeout(crashCollapseTimerRef.current)

    setCrashEffect(nextEffect)
    if (nextEffect === 'earthquake') {
      setIsCollapsing(true)
    } else {
      crashCollapseTimerRef.current = setTimeout(() => setIsCollapsing(true), CRASH_BIRD_CONTACT_DELAY)
    }
    crashEffectTimerRef.current = setTimeout(() => setCrashEffect(null), nextEffect === 'earthquake' ? 560 : CRASH_BIRD_DURATION)
  }, [])

  useEffect(() => () => {
    clearDropWatchdog()
    if (crashEffectTimerRef.current) clearTimeout(crashEffectTimerRef.current)
    if (crashCollapseTimerRef.current) clearTimeout(crashCollapseTimerRef.current)
  }, [clearDropWatchdog])

  const {
    status,
    isHotRollsActive,
    hotRollsEndTime,
    currentStake,
    tower,
    hasStability,
    // Server cycle params
    currentCycleId,
    cyclePeriodMs,
    cycleAmplitude,
    cyclePhaseShiftMs,
    cycleOriginX,
    cycleStartedAt,
    cycleStartedAtClientTime,
    currentRollType,
    // Server actions
    applyServerState,
    applyRollResult,
    applyGameOver,
    applyCashOut,
    // Demo actions
    startGame,
    addRoll,
    updateStake,
    checkHotRollsTrigger,
    useStability,
    cashOut,
    gameOver,
    resetGame,
  } = useGameStore()

  const { isDemoMode, sessionId } = useSessionStore()
  const isPlaying = status === 'playing' || status === 'hot_rolls'
  const isGameOver = status === 'game_over' || status === 'cashed_out'
  const useServerMode = wsConnected && !isDemoMode

  // ==========================================
  // WS CONNECTION
  // ==========================================
  // Use a ref so the WS connection effect doesn't re-run when handler deps change.
  const handleServerEventRef = useRef<((event: ServerEvent) => void) | null>(null)
  const handleServerEvent = useCallback((event: ServerEvent) => {
    // TS backend sends flat messages: { type, ...fields } — fields are top-level
    const e = event as unknown as Record<string, unknown>
    switch (event.type) {
      case 'config': {
        const cur = e.currency as { code?: string } | undefined
        const serverPresets = (e.bet_presets as number[]) || []
        const limits = e.bet_limits as { min: number; max: number } | undefined
        const minBet = limits?.min ?? 10
        const maxBet = limits?.max ?? GAME_CONFIG.MAX_BET
        const defaultBet = Math.min(Math.max(DEFAULT_BET_MINOR, minBet), maxBet)
        const currencyCode = typeof cur?.code === 'string' ? cur.code.trim().toUpperCase() : ''
        useSessionStore.setState({
          ...(currencyCode ? { currencyCode } : {}),
          presets: CALCULATOR_PRESETS_MINOR,
          minBet,
          maxBet,
          defaultBet,
        })
        console.log('[GameWS] Config:', cur, 'presets:', serverPresets, 'limits:', limits)
        break
      }
      case 'balance_update': {
        const currencyCode = typeof e.currency === 'string' ? e.currency.trim().toUpperCase() : ''
        if (currencyCode) {
          useSessionStore.setState({ currencyCode })
        }
        if (typeof e.balance === 'number') {
          useGameStore.getState().setBalance(e.balance)
        }
        break
      }
      case 'game_state':
        terminalConnectionErrorRef.current = null
        onConnectionStatusChange?.('ready')
        applyServerState(e as unknown as ServerSessionPayload)
        break
      case 'roll_result': {
        clearDropWatchdog()
        const rollPayload = e as unknown as ServerRollResultPayload
        // Capture receive time NOW so that `hot_rolls_ms_left` can be anchored
        // to message arrival, not to the deferred apply-time below. Without
        // this, the HOT ROLLS banner would be ~750ms out of sync with the
        // server expiry timer and disappear early.
        const arrivedAt = Date.now()

        // Authoritative landing X from server.
        const serverOffsetPx = (rollPayload.landing_center_offset ?? 0) * PIXELS_PER_UNIT
        const towerState = useGameStore.getState().tower
        const previousTowerX = towerState.length > 0 ? towerState[towerState.length - 1].positionX : 0
        const serverLandingX = previousTowerX + serverOffsetPx

        const isRollOff = rollPayload.outcome === 'roll_off'
        const isWipeout = rollPayload.outcome === 'success' && rollPayload.session?.phase === 'game_over'
        const isGameOver = rollPayload.outcome !== 'success'

        setDroppingRoll((current) => current ? {
          ...current,
          // Keep the landing target chosen at DROP time. Updating it while
          // the CSS transition is already running causes a visible hitch on
          // fast server responses. The computed value uses the same cycle
          // math as the backend, so this is normally identical.
          landingXOverride: current.landingXOverride ?? serverLandingX,
          isEdgeMiss: isGameOver && !isRollOff,
          isRollOff,
          isWipeout,
        } : current)

        if (rollPayload.outcome === 'success' && !isWipeout) {
          // Normal landing: swap to tower after fall + squash + bounce
          const delay = BASE_FALL_DURATION + 150
          setTimeout(() => {
            setDroppingRoll(null)
            applyRollResult(rollPayload, arrivedAt)
          }, delay)
        } else if (isWipeout) {
          // The roll physically lands, but the cumulative win is drained to 0.
          // Crash the visible stack instead of presenting this as a normal
          // successful roll with a coefficient.
          const totalAnimTime = BASE_FALL_DURATION + 950
          setTimeout(() => {
            triggerCrashEffect()
          }, BASE_FALL_DURATION + 160)
          setTimeout(() => {
            setDroppingRoll(null)
            if (rollPayload.session) applyGameOver(rollPayload.session as ServerSessionPayload, arrivedAt)
            setIsDoorOpen(false)
            setResultType('lose')
            setResultAmount(0)
            setShowResult(true)
          }, totalAnimTime)
        } else if (isRollOff) {
          setTimeout(triggerCrashEffect, BASE_FALL_DURATION + 280)
          setTimeout(() => {
            setDroppingRoll(null)
            if (rollPayload.session) applyGameOver(rollPayload.session as ServerSessionPayload, arrivedAt)
            setIsDoorOpen(false)
            setResultType('lose')
            setResultAmount(0)
            setShowResult(true)
          }, ROLL_OFF_ANIMATION_TIME)
        } else {
          // Touch + slide off (~500ms slide + fade), then game over
          const totalAnimTime = BASE_FALL_DURATION + 800
          setTimeout(() => {
            triggerCrashEffect('earthquake')
          }, BASE_FALL_DURATION + 100)
          setTimeout(() => {
            setDroppingRoll(null)
            if (rollPayload.session) applyGameOver(rollPayload.session as ServerSessionPayload, arrivedAt)
            setIsDoorOpen(false)
            setResultType('lose')
            setResultAmount(0)
            setShowResult(true)
          }, totalAnimTime)
        }
        break
      }
      case 'cash_out_result':
        if (e.session) applyCashOut(e.session as ServerSessionPayload)
        setResultType('win')
        setResultAmount(typeof e.payout === 'number' ? e.payout : currentStake)
        setIsDoorOpen(false)
        setShowResult(true)
        break
      case 'game_over':
        if (e.session) applyGameOver(e.session as ServerSessionPayload)
        break
      case 'hot_rolls_event':
        break
      case 'error': {
        console.error('[GameWS] Server error:', e.message ?? e)
        const code = String(e.code ?? '')
        const message = String(e.message ?? 'WebSocket server error')
        setWsError(message)
        if (isConnectionSetupError(code)) {
          const userMessage = connectionErrorMessage(code, message)
          terminalConnectionErrorRef.current = userMessage
          onConnectionStatusChange?.('error', userMessage)
        }
        if (code === 'INVALID_ACTION' && (message === 'cycle mismatch' || message === 'cannot drop in current phase')) {
          unlockDrop()
          try {
            getGameWs()?.requestState()
          } catch {
            setWsConnected(false)
          }
          break
        }
        setWsConnected(false)
        unlockDrop()
        break
      }
      case 'pong':
        break
      default:
        break
    }
  }, [applyServerState, applyRollResult, applyCashOut, applyGameOver, currentStake, triggerCrashEffect, clearDropWatchdog, unlockDrop, onConnectionStatusChange])

  // Keep the ref pointing at the latest handler so the stable WS callback works.
  useEffect(() => {
    handleServerEventRef.current = handleServerEvent
  }, [handleServerEvent])

  const connectGameWs = useCallback(async (): Promise<GameWebSocketService | null> => {
    if (isDemoMode || !sessionId || !WS_BASE_URL) {
      setWsConnected(false)
      return null
    }

    terminalConnectionErrorRef.current = null
    onConnectionStatusChange?.('connecting')

    const currentWs = getGameWs()
    if (currentWs?.connected) {
      return currentWs
    }
    if (currentWs?.connecting) {
      return null
    }

    let ws: GameWebSocketService
    ws = createGameWs({
      baseUrl: WS_BASE_URL,
      sessionId,
      token: WS_TOKEN || sessionId,
      onEvent: (event) => handleServerEventRef.current?.(event),
      onClose: (event) => {
        if (getGameWs() !== ws) return
        setWsConnected(false)
        unlockDrop()
        if (!useGameStore.getState().wsSessionId) {
          const message = terminalConnectionErrorRef.current
            || event.reason
            || 'CONNECTION FAILED'
          onConnectionStatusChange?.('error', message)
        }
      },
    })

    try {
      await ws.connect()
      if (getGameWs() !== ws) return null
      setWsError(null)
      setWsConnected(true)
      onConnectionStatusChange?.('connected')
      console.log('[GameWS] Connected')
      return ws
    } catch (err) {
      if (getGameWs() !== ws) return null
      console.warn('[GameWS] Connection failed:', err)
      const message = err instanceof Error ? err.message : String(err)
      setWsError(message)
      setWsConnected(false)
      onConnectionStatusChange?.('error', 'CONNECTION FAILED')
      return null
    }
  }, [isDemoMode, sessionId, unlockDrop, onConnectionStatusChange])

  // Connect WS when we have a real session
  useEffect(() => {
    void connectGameWs()

    return () => {
      destroyGameWs()
      setWsConnected(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectGameWs, connectionRetryKey])

  useEffect(() => {
    if (isDemoMode || !sessionId) return

    const recoverConnection = () => {
      if (document.visibilityState === 'hidden') return
      const ws = getGameWs()
      if (ws?.connected) {
        try {
          ws.requestState()
          return
        } catch {
          setWsConnected(false)
        }
      }
      if (ws?.connecting) return
      unlockDrop()
      void connectGameWs()
    }

    document.addEventListener('visibilitychange', recoverConnection)
    window.addEventListener('focus', recoverConnection)
    return () => {
      document.removeEventListener('visibilitychange', recoverConnection)
      window.removeEventListener('focus', recoverConnection)
    }
  }, [connectGameWs, isDemoMode, sessionId, unlockDrop])

  // ==========================================
  // ROLL VARIANT from server
  // Server spawns the cycle at session-create time and reuses it on BET IN,
  // so currentRollType stays stable across idle → playing transition.
  // ==========================================
  useEffect(() => {
    if (useServerMode && currentRollType) {
      setCurrentRollVariant(rollTypeToVariant(currentRollType))
    }
  }, [useServerMode, currentRollType])

  // ==========================================
  // DOOR OPEN on mount
  // ==========================================
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsDoorOpen(true)
      fadeToMusic('gameplay')
    }, 500)
    return () => clearTimeout(timer)
  }, [fadeToMusic])

  // ==========================================
  // DEMO MODE: generate random roll
  // ==========================================
  const generateNewRoll = useCallback(() => {
    if (useServerMode) return // Server controls roll type
    if (isHotRollsActive) {
      setCurrentRollVariant(Math.random() > 0.5 ? 6 : 7)
    } else {
      setCurrentRollVariant((Math.floor(Math.random() * 5) + 1) as RollVariant)
    }
  }, [isHotRollsActive, useServerMode])

  useEffect(() => {
    if (!useServerMode) generateNewRoll()
  }, [])

  const prevHotRef = useRef(isHotRollsActive)
  useEffect(() => {
    if (isPlaying && isHotRollsActive !== prevHotRef.current && !useServerMode) {
      generateNewRoll()
    }
    prevHotRef.current = isHotRollsActive
  }, [isHotRollsActive, isPlaying, generateNewRoll, useServerMode])

  // Client-side HOT ROLLS expiry: server sets isHotRollsActive=true with
  // hotRollsEndTime, but only flips it back at the next drop. We need to
  // hide the hot roll on the chopsticks the moment the timer expires.
  useEffect(() => {
    if (!isHotRollsActive || !hotRollsEndTime) return
    const msLeft = hotRollsEndTime - Date.now()
    const expire = () => {
      useGameStore.setState((state) => ({
        isHotRollsActive: false,
        hotRollsEndTime: null,
        status: state.status === 'hot_rolls' ? 'playing' : state.status,
      }))
      // Do NOT pick a random variant locally in server mode. The server
      // respawns the cycle as non-hot and pushes a fresh game_state with
      // the authoritative roll_type; the currentRollType useEffect below
      // syncs `currentRollVariant` from that. A local random pick races
      // with the server push and can desync the chopstick/falling-roll
      // variant from the one the server resolves — making the roll look
      // like one color mid-fall and a different one after it lands.
      if (!useServerMode) {
        setCurrentRollVariant((Math.floor(Math.random() * 5) + 1) as RollVariant)
      }
    }
    if (msLeft <= 0) {
      expire()
      return
    }
    const timer = setTimeout(expire, msLeft)
    return () => clearTimeout(timer)
  }, [isHotRollsActive, hotRollsEndTime, useServerMode])

  // Panel auto-switch
  useEffect(() => {
    if (isPlaying && bottomMode === 'bet') setBottomMode('control')
    if (status === 'idle' && bottomMode === 'control' && !showResult && !isDropping) {
      setBottomMode('bet')
    }
    // The reverse switch (back to BET) is intentionally NOT here.
    // `handleResultClose` performs it explicitly when the player dismisses
    // the result banner. Doing it on `isGameOver` would expose BetPanel
    // for ~1400ms during the loss animation (before ResultBanner mounts),
    // letting the player click BET IN while the previous round is still
    // wrapping up — which leaves the new bet stuck on a stale phase.
  }, [status, isPlaying, isGameOver, bottomMode, showResult, isDropping])

  // ==========================================
  // TOWER HEIGHT CALCULATION
  // ==========================================
  const overlap = GAME_CONFIG.TOWER_OVERLAP
  let towerTranslateY = tower.length > 0 ? TOWER_EXTRA_OFFSET : 0
  const scrollRolls = Math.max(0, tower.length - 2)
  for (let i = 0; i < scrollRolls; i++) {
    towerTranslateY += ROLL_DIMENSIONS[tower[i].variant].height * overlap
  }

  let towerHeightPx = 0
  for (const roll of tower) {
    towerHeightPx += ROLL_DIMENSIONS[roll.variant].height * overlap
  }
  let translateYAtDrop = 0
  for (let i = 0; i < Math.max(0, tower.length - 2); i++) {
    translateYAtDrop += ROLL_DIMENSIONS[tower[i].variant].height * overlap
  }
  const visualTowerHeight = towerHeightPx - translateYAtDrop - (tower.length > 0 ? TOWER_EXTRA_OFFSET : 0)

  // ==========================================
  // BET IN
  // ==========================================
  const handleBet = useCallback(async (amount: number) => {
    setIsDropping(false)
    dropLockRef.current = false

    if (!isDemoMode && sessionId) {
      let ws = getGameWs()
      if (!ws?.connected) {
        ws = await connectGameWs()
      }
      if (!ws) return
      try {
        ws.placeBet(amount)
        // State will update via game_state event handler
      } catch (err) {
        console.error('[GameWS] placeBet failed:', err)
        setWsConnected(false)
        setWsError(err instanceof Error ? err.message : String(err))
        return
      }
    } else if (isDemoMode) {
      startGame()
    } else {
      console.warn('[GameWS] BET ignored: server connection is not ready', wsError)
      return
    }
    setShowTutorial(false)
    setBottomMode('control')
  }, [connectGameWs, isDemoMode, sessionId, startGame, wsError])

  // ==========================================
  // DROP
  // ==========================================
  const handleDrop = useCallback(async () => {
    if (!isPlaying || isDropping || dropLockRef.current) return

    let serverWs: GameWebSocketService | null = null
    if (!isDemoMode && sessionId) {
      serverWs = getGameWs()
      if (!serverWs?.connected) {
        await connectGameWs()
        unlockDrop()
        return
      }
    }

    dropLockRef.current = true
    setIsDropping(true)

    let dropPositionPx = chopsticksPositionRef.current
    const rollVariant = currentRollVariant
    const fallDuration = BASE_FALL_DURATION

    if (serverWs?.connected) {
      if (!currentCycleId) {
        try {
          serverWs.requestState()
        } catch {
          setWsConnected(false)
          void connectGameWs()
        }
        unlockDrop()
        return
      }

      // === SERVER MODE ===
      // Use client-local cycle start time to avoid clock skew with server
      const cycleStart = cycleStartedAtClientTime || Date.now()
      const dropNow = Date.now()
      const releaseMs = Math.max(0, dropNow - cycleStart)
      let landingXOverride: number | undefined

      if (cyclePeriodMs && typeof cycleAmplitude === 'number') {
        const angle = 2 * Math.PI * ((releaseMs + (cyclePhaseShiftMs ?? 0)) / cyclePeriodMs)
        const origin = cycleOriginX ?? 0
        const position = origin + cycleAmplitude * Math.sin(angle)
        const omega = 2 * Math.PI / (cyclePeriodMs / 1000)
        const velocity = cycleAmplitude * omega * Math.cos(angle)
        const landingCenter = position + velocity * (fallDuration / 1000) * 0.25

        dropPositionPx = position * PIXELS_PER_UNIT
        landingXOverride = landingCenter * PIXELS_PER_UNIT
      }

      // Start optimistic fall animation immediately
      setDroppingRoll({
        variant: rollVariant,
        isHot: isHotRollsActive,
        startX: dropPositionPx,
        targetY: visualTowerHeight,
        fallDuration,
        horizontalVelocity: chopsticksVelocityRef.current,
        landingXOverride,
      })

      setTimeout(() => {
        playSfx('land')
        vibrateOnLand()
      }, Math.max(fallDuration - 250, 50))

      try {
        serverWs.drop(releaseMs, currentCycleId || '')
        clearDropWatchdog()
        dropWatchdogRef.current = setTimeout(() => {
          console.warn('[GameWS] DROP response timeout, reconnecting')
          unlockDrop()
          const ws = getGameWs()
          if (ws?.connected) {
            try {
              ws.requestState()
              return
            } catch {
              setWsConnected(false)
            }
          }
          if (!ws?.connecting) {
            setWsConnected(false)
            void connectGameWs()
          }
        }, 5000)
        // FallingRoll cleanup + tower update happen atomically inside the
        // roll_result event handler. No need to clear droppingRoll here.
      } catch (err) {
        console.error('[GameWS] drop failed:', err)
        setWsConnected(false)
        unlockDrop()
        return
      }
    } else if (isDemoMode) {
      // === DEMO MODE (original local logic) ===
      const rollWidth = ROLL_DIMENSIONS[rollVariant].width
      const previousRoll = tower.length > 0 ? tower[tower.length - 1] : null
      const isFirstRoll = tower.length === 0
      let coefficient = 1
      let isGameOverFlag = false
      let gameOverReason: 'miss' | 'edge' = 'miss'
      let centerOffsetPercent = 0
      let stabilitySavedEdge = false

      // Inertia: roll drifts horizontally during fall
      const velocity = chopsticksVelocityRef.current
      const INERTIA_FACTOR = 0.25
      const inertiaDriftPx = velocity * (BASE_FALL_DURATION / 1000) * INERTIA_FACTOR
      let finalPositionPx = dropPositionPx + inertiaDriftPx

      if (isFirstRoll) {
        const PLATE_WIDTH = 292
        const plateEdge = PLATE_WIDTH / 2
        const rollEdge = Math.abs(finalPositionPx) + rollWidth / 2
        if (rollEdge > plateEdge) {
          isGameOverFlag = true; gameOverReason = 'miss'; coefficient = 0
        } else {
          coefficient = 1.0
        }
      } else {
        const previousCenterPx = previousRoll!.positionX
        const previousRollWidth = ROLL_DIMENSIONS[previousRoll!.variant].width
        const centerDiffPx = Math.abs(finalPositionPx - previousCenterPx)
        const previousEdgePx = previousRollWidth / 2
        centerOffsetPercent = (centerDiffPx / previousEdgePx) * 100

        if (centerOffsetPercent > 100) {
          const STABILITY_MAX_OFFSET = 180
          if (hasStability && centerOffsetPercent <= STABILITY_MAX_OFFSET) {
            coefficient = 0.1 + Math.random() * 0.4
            const direction = finalPositionPx > previousCenterPx ? 1 : -1
            finalPositionPx = previousCenterPx + direction * (previousEdgePx - rollWidth * 0.3)
            gameOverReason = 'edge'
            stabilitySavedEdge = true
            useStability()
          } else {
            isGameOverFlag = true; gameOverReason = 'miss'; coefficient = 0
          }
        } else {
          if (centerOffsetPercent <= 0) coefficient = 1.5 + Math.random() * 0.5
          else if (centerOffsetPercent <= 10) coefficient = 1.25 + Math.random() * 0.5
          else if (centerOffsetPercent <= 20) coefficient = 1.0 + Math.random() * 0.5
          else if (centerOffsetPercent <= 30) coefficient = 0.75 + Math.random() * 0.5
          else if (centerOffsetPercent <= 40) coefficient = 0.51 + Math.random() * 0.49
          else if (centerOffsetPercent <= 50) coefficient = 0.25 + Math.random() * 0.75
          else if (centerOffsetPercent <= 60) coefficient = 0.25 + Math.random() * 0.5
          else if (centerOffsetPercent <= 70) coefficient = 0.1 + Math.random() * 0.65
          else if (centerOffsetPercent <= 80) coefficient = 0.1 + Math.random() * 0.4
          else if (centerOffsetPercent <= 90) coefficient = 0.1 + Math.random() * 0.15
          else coefficient = 0.1
        }
      }

      if (isHotRollsActive && !isGameOverFlag) coefficient = Math.max(coefficient, 1.5)
      const isBonusRoll = (tower.length + 1) % GAME_CONFIG.BONUS_LINE_INTERVAL === 0
      if (isBonusRoll && !isGameOverFlag) coefficient *= 2
      if (Math.round(coefficient * 100) === 50) coefficient = 0.49

      const isEdgeMiss = isGameOverFlag && !isFirstRoll && centerOffsetPercent > 100 && centerOffsetPercent <= 180
      const shouldRollOff = !isGameOverFlag
        && !isFirstRoll
        && !stabilitySavedEdge
        && Math.random() < DEMO_ROLL_OFF_CHANCE

      setDroppingRoll({
        variant: rollVariant, isHot: isHotRollsActive, startX: dropPositionPx,
        targetY: visualTowerHeight,
        isMiss: isGameOverFlag && !isEdgeMiss, isEdgeMiss, isRollOff: shouldRollOff, fallDuration,
        horizontalVelocity: chopsticksVelocityRef.current,
      })

      setTimeout(() => { playSfx('land'); vibrateOnLand() }, Math.max(fallDuration - 250, 50))

      if (isEdgeMiss) setTimeout(() => {
        triggerCrashEffect('earthquake')
      }, fallDuration + 100)

      const totalAnimTime = shouldRollOff ? ROLL_OFF_ANIMATION_TIME : (isEdgeMiss ? fallDuration + 800 : fallDuration + 150)
      await new Promise(resolve => setTimeout(resolve, totalAnimTime))
      setDroppingRoll(null)

      if (shouldRollOff) {
        triggerCrashEffect()
        setIsDoorOpen(false)
        gameOver('miss')
        setResultType('lose'); setResultAmount(0); setShowResult(true)
      } else if (isGameOverFlag) {
        if (!isEdgeMiss) {
          triggerCrashEffect('earthquake')
        }
        setIsDoorOpen(false)
        gameOver(gameOverReason)
        setResultType('lose'); setResultAmount(0); setShowResult(true)
      } else {
        addRoll({ variant: rollVariant, type: isHotRollsActive ? 'hot' : 'normal', positionX: finalPositionPx, coefficient, isBonus: isBonusRoll })
        const isZeroStake = updateStake(coefficient)
        if (isZeroStake) {
          setIsCollapsing(true); setIsDoorOpen(false)
          setIsDropping(false); dropLockRef.current = false
          gameOver('miss'); setResultType('lose'); setResultAmount(0); setShowResult(true); setIsCollapsing(false)
          return
        }
        if (!isHotRollsActive) checkHotRollsTrigger()
        generateNewRoll()
      }
    } else {
      console.warn('[GameWS] DROP ignored: server connection is not ready', wsError)
      setIsDropping(false)
      dropLockRef.current = false
      return
    }

    setTimeout(() => {
      setIsDropping(false)
      dropLockRef.current = false
    }, GAME_CONFIG.NEW_ROLL_DELAY)

  }, [isPlaying, isDropping, currentRollVariant, isHotRollsActive, tower,
    hasStability, useServerMode, cycleStartedAtClientTime, currentCycleId, visualTowerHeight,
    cyclePeriodMs, cycleAmplitude, cyclePhaseShiftMs, cycleOriginX,
    addRoll, updateStake, checkHotRollsTrigger, gameOver, generateNewRoll,
    applyRollResult, applyGameOver, useStability, playSfx, vibrateOnLand, triggerCrashEffect,
    isDemoMode, sessionId, wsError, connectGameWs, unlockDrop, clearDropWatchdog])

  // ==========================================
  // CASH OUT
  // ==========================================
  const handleCashOut = useCallback(async () => {
    // Guard against clicks during the bet→server-confirm window: the cash
    // out button is no longer visually disabled there (to avoid the opacity
    // flash on BET IN), so the handler enforces the guard itself.
    if (!isPlaying || isDropping) return
    if (useServerMode) {
      const ws = getGameWs()
      if (!ws) return
      setIsDoorOpen(false)
      try {
        ws.cashOut()
        // cash_out_result handled by handleServerEvent
      } catch (err) {
        console.error('[GameWS] cashOut failed:', err)
      }
    } else if (isDemoMode) {
      const winAmount = currentStake
      setIsDoorOpen(false)
      cashOut()
      setResultType('win')
      setResultAmount(winAmount)
      setShowResult(true)
    } else {
      console.warn('[GameWS] CASH OUT ignored: server connection is not ready', wsError)
    }
  }, [useServerMode, isDemoMode, currentStake, cashOut, isPlaying, isDropping, wsError])

  // ==========================================
  // RESTART
  // ==========================================
  const handleResultClose = useCallback(() => {
    setShowResult(false)
    setIsDropping(false)
    dropLockRef.current = false
    setDroppingRoll(null)
    setIsCollapsing(false)
    setCrashEffect(null)
    if (crashEffectTimerRef.current) clearTimeout(crashEffectTimerRef.current)
    if (crashCollapseTimerRef.current) clearTimeout(crashCollapseTimerRef.current)
    resetGame()
    setBottomMode('bet')
    setShowTutorial(true)
    setTimeout(() => setIsDoorOpen(true), 300)
  }, [resetGame])

  const handleHistoryToggle = () => {
    if (bottomMode === 'history') setBottomMode(isPlaying ? 'control' : 'bet')
    else setBottomMode('history')
  }

  return (
    <div className="w-full h-full flex flex-col relative overflow-hidden bg-[#1a1a2e]">
      <div className={`absolute inset-0 z-0 ${crashEffect === 'earthquake' ? 'crash-earthquake' : ''}`} style={{ backgroundImage: `url(${bgMobile})`, backgroundSize: 'cover', backgroundPosition: 'center center' }} />
      {crashEffect === 'bird' && (
        <img
          src={cartoonBird}
          alt=""
          aria-hidden="true"
          className="crash-bird-fly pointer-events-none absolute z-[45] select-none"
          style={{
            width: 'clamp(92px, 28vw, 150px)',
            top: '42%',
            left: '50%',
            filter: 'drop-shadow(0 8px 10px rgba(0,0,0,0.35))',
          }}
        />
      )}

      <SlidingDoors isOpen={isDoorOpen} config={doorConfig} />

      <div className="h-[60px] relative z-10 flex items-center justify-center">
        {isHotRollsActive && <HotRollsBanner />}
      </div>

      <div className="flex-1 relative z-10 flex flex-col items-center justify-end pb-4 game-area">
        {showTutorial && isDoorOpen && (
          <div
            className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 z-30 text-center px-6 animate-pulse"
            style={{ fontFamily: "'Caesar Dressing', cursive", fontWeight: 400, fontSize: '31px', lineHeight: '130%', color: '#A52A1F', textShadow: '0 2px 6px rgba(0,0,0,0.5)', maxWidth: '320px' }}
          >
            Make your BET
          </div>
        )}

        {(isPlaying || status === 'idle') && isDoorOpen && !showResult && (
          <div className="absolute inset-0 z-40 pointer-events-none" style={{ top: '-50px', willChange: 'transform' }}>
            <Chopsticks
              isHot={isHotRollsActive}
              rollVariant={currentRollVariant}
              onPositionChange={handleChopsticksPosition}
              config={chopsticksConfig}
              isDropping={isDropping}
              isFirstRoll={tower.length === 0}
              // Server cycle sync
              serverCycleStartedAt={useServerMode ? cycleStartedAt : null}
              serverCycleStartedAtClientTime={useServerMode ? cycleStartedAtClientTime : undefined}
              serverCyclePeriodMs={useServerMode ? cyclePeriodMs : undefined}
              serverCyclePhaseShiftMs={useServerMode ? cyclePhaseShiftMs : undefined}
              serverCycleAmplitude={useServerMode ? cycleAmplitude : undefined}
              serverCycleOriginX={useServerMode ? cycleOriginX : undefined}
            />
          </div>
        )}

        {droppingRoll && (
          <div className="absolute inset-0 z-[35] pointer-events-none">
            <FallingRoll
              variant={droppingRoll.variant}
              isHot={droppingRoll.isHot}
              startX={droppingRoll.startX}
              towerHeight={droppingRoll.targetY}
              rollTop={chopsticksConfig.roll.top}
              config={fallingRollConfig}
              isMiss={droppingRoll.isMiss}
              isEdgeMiss={droppingRoll.isEdgeMiss}
              isRollOff={droppingRoll.isRollOff}
              isWipeout={droppingRoll.isWipeout}
              fallDuration={droppingRoll.fallDuration}
              horizontalVelocity={droppingRoll.horizontalVelocity}
              landingXOverride={droppingRoll.landingXOverride}
            />
          </div>
        )}

        <div
          className="absolute inset-0 transition-transform duration-700 ease-out"
          style={{ transform: `translateY(${towerTranslateY}px)`, willChange: 'transform' }}
        >
          <Tower config={fallingRollConfig} isCollapsing={isCollapsing} />
          <Plate config={plateConfig} />
        </div>
      </div>

      <div className="relative z-20 flex flex-col">
        {/* Keep BetPanel and ControlPanel mounted; toggle visibility instead
            of remount/unmount to avoid the flicker on phase transition. */}
        <div style={{ display: bottomMode === 'bet' ? 'block' : 'none' }}>
          <BetPanel onBet={handleBet} />
        </div>
        <div style={{ display: bottomMode === 'control' ? 'block' : 'none' }}>
          <ControlPanel onCashOut={handleCashOut} onShop={() => setIsShopOpen(true)} onDrop={handleDrop} isDropping={isDropping} />
        </div>
        {bottomMode === 'history' && <HistoryPanel onClose={() => setBottomMode(isPlaying ? 'control' : 'bet')} />}
        <BottomPanel onHistoryOpen={handleHistoryToggle} onInfoOpen={() => setIsInfoOpen(prev => !prev)} isInfoOpen={isInfoOpen} isHistoryOpen={bottomMode === 'history'} />
      </div>

      <Shop isOpen={isShopOpen} onClose={() => setIsShopOpen(false)} />
      <InfoModal isOpen={isInfoOpen} onClose={() => setIsInfoOpen(false)} />
      {showResult && <ResultBanner type={resultType} amount={resultAmount} onClose={handleResultClose} />}
    </div>
  )
}
